/**
 * Service de stockage pour la démo
 * Données en mémoire + persistance localStorage pour survivre aux rechargements
 */

import { getAllSeedEntities, normalizeRecord } from '../config/demoEntityRegistry';
import { compareByNewestFirst } from '../../shared/utils/listSortUtils';
import { normalizeRecordStatus } from '../../shared/utils/recordStatusUtils';
import { synthesizeAllMockData } from './demoMockSynthesizer';
import i18n from '../../i18n';

const demoClientFallback = (index) => i18n.t('onlineDemo.demoStorage.demo_client', { index });
const ds = (key, opts) => i18n.t(`onlineDemo.demoStorage.${key}`, opts);

const DEMO_ENTITY_PERSIST_KEY = 'qawaem_demo_entity_db_v7';

/** Seed files generated with trailing-slash paths use a `_` suffix bucket. */
const ENTITY_BUCKET_ALIASES = {
  hr_leaves: 'hr_leaves_',
  hr_training: 'hr_training_',
  hr_evaluations: 'hr_evaluations_',
  hr_recruitment: 'hr_recruitment_',
  hr_contracts: 'hr_contracts_',
  facturation_services: 'facturation_services_',
  livraison_ongoing: 'livraison_ongoing_',
  livraison_delivery_proofs: 'livraison_delivery_proofs_',
  livraison_delivery_statuses: 'livraison_delivery_statuses_',
  livraison_real_time_tracking: 'livraison_real_time_tracking_',
  livraison_refused_deliveries: 'livraison_refused_deliveries_',
  livraison_route_sheets: 'livraison_route_sheets_',
  livraison_vehicle_loading: 'livraison_vehicle_loading_',
  fleet_drivers: 'fleet_drivers_',
  fleet_vehicles: 'fleet_vehicles_',
  distribution_shipments: 'distribution_shipments_',
  distribution_complaints: 'distribution_complaints_',
};

const DEMO_EMPLOYEE_NAMES = [
  'جاسم المطيري', 'سارة العتيبي', 'راشد القحطاني', 'نورة الحربي', 'فيصل الدوسري',
  'ليلى الزهراني', 'داود الشمري', 'ريم العمري', 'وليد الحازمي', 'منى السبيعي',
];

class DemoStorageService {
  constructor() {
    this.data = {};
    this.isInitialized = false;
    this.initPromise = null;
    /** Only mutated entity buckets are written to localStorage (avoids 5MB quota on 600+ seed entities). */
    this.dirtyKeys = new Set();
  }

  _markDirty(bucket) {
    if (bucket) this.dirtyKeys.add(bucket);
    const alias = ENTITY_BUCKET_ALIASES[bucket];
    if (alias) {
      delete this.data[alias];
    }
    for (const [canonical, aliasKey] of Object.entries(ENTITY_BUCKET_ALIASES)) {
      if (aliasKey === bucket) {
        this.dirtyKeys.add(canonical);
        delete this.data[aliasKey];
      }
    }
  }

  _persist() {
    try {
      let snap = {};
      try {
        const raw = localStorage.getItem(DEMO_ENTITY_PERSIST_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            snap = parsed;
          }
        }
      } catch (_) {
        /* start with empty overlay */
      }

      for (const key of this.dirtyKeys) {
        if (this.data[key] !== undefined) {
          snap[key] = this.data[key];
        }
      }

      localStorage.setItem(DEMO_ENTITY_PERSIST_KEY, JSON.stringify(snap));
    } catch (e) {
      if (e && e.name === 'QuotaExceededError') {
        try {
          const minimal = {};
          for (const key of this.dirtyKeys) {
            if (this.data[key] !== undefined) minimal[key] = this.data[key];
          }
          localStorage.setItem(DEMO_ENTITY_PERSIST_KEY, JSON.stringify(minimal));
          console.warn('[demo] quota exceeded; persisted dirty keys only:', Object.keys(minimal));
        } catch (e2) {
          console.warn('[demo] persist failed after quota fallback:', e2);
        }
      } else {
        console.warn('[demo] persist failed:', e);
      }
    }
  }

  _hydrateFromLocalStorage() {
    try {
      const raw = localStorage.getItem(DEMO_ENTITY_PERSIST_KEY);
      if (!raw) return;
      const snap = JSON.parse(raw);
      if (!snap || typeof snap !== 'object' || Array.isArray(snap)) return;
      for (const k of Object.keys(snap)) {
        this.data[k] = snap[k];
      }
      console.log('[demo] Restored entity DB from localStorage:', Object.keys(snap));
    } catch (e) {
      console.warn('[demo] hydrate from localStorage failed:', e);
    }
  }

  _stampTimestamps(item, isUpdate = false) {
    const now = new Date().toISOString();
    const out = { ...item };
    if (!isUpdate) {
      out.createdAt = out.createdAt || out.created_at || now;
      out.created_at = out.created_at || out.createdAt;
    }
    out.updatedAt = now;
    out.updated_at = now;
    return out;
  }

  async initialize() {
    if (this.isInitialized) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._loadInitialData();
    await this.initPromise;
    this.isInitialized = true;

    console.log('✅ DemoStorageService initialized with data:', Object.keys(this.data));
  }

  _normalizeHrDemoRecord(entity, record, index) {
    if (!record || typeof record !== 'object') return record;
    const i = index || 0;
    const employeeName = record.employee_name || record.employeeName || record.name || DEMO_EMPLOYEE_NAMES[i % DEMO_EMPLOYEE_NAMES.length];

    if (entity === 'hr_leaves' || entity === 'hr_leaves_') {
      return {
        ...record,
        employee_name: employeeName,
        leave_type: record.leave_type || record.type || ['إجازة سنوية', 'إجازة مرضية', 'إجازة تعويضية'][i % 3],
        start_date: record.start_date || record.startDate || record.start || `2026-0${(i % 6) + 1}-01`,
        end_date: record.end_date || record.endDate || record.end || `2026-0${(i % 6) + 1}-05`,
        days: Number(record.days) || 3 + (i % 5),
        status: ['pending', 'approved', 'rejected'][i % 3],
      };
    }

    if (entity === 'hr_training' || entity === 'hr_training_') {
      let participants = record.participants;
      if (typeof participants === 'string' || typeof participants === 'number') {
        participants = Array.from({ length: Math.max(1, Number(participants) || 3) }, (_, j) => DEMO_EMPLOYEE_NAMES[j % DEMO_EMPLOYEE_NAMES.length]);
      }
      if (!Array.isArray(participants)) participants = DEMO_EMPLOYEE_NAMES.slice(0, 3);
      return {
        ...record,
        title: record.title || record.training || `جلسة تدريب ${i + 1}`,
        trainer: record.trainer || DEMO_EMPLOYEE_NAMES[(i + 2) % DEMO_EMPLOYEE_NAMES.length],
        start_date: record.start_date || record.startDate || record.start || `2026-0${(i % 6) + 1}-10`,
        end_date: record.end_date || record.endDate || record.end || `2026-0${(i % 6) + 1}-12`,
        duration: record.duration || `${8 + (i % 4)}h`,
        participants,
        cost: Number(record.cost) || (i + 1) * 1500,
        status: ['planned', 'in_progress', 'completed'][i % 3],
        category: record.category || 'عام',
        location: record.location || 'المقر الرئيسي',
      };
    }

    if (entity === 'hr_evaluations' || entity === 'hr_evaluations_') {
      const note = 3 + ((i % 5) * 0.3);
      return {
        ...record,
        employee_name: employeeName,
        evaluator: record.evaluator || DEMO_EMPLOYEE_NAMES[(i + 4) % DEMO_EMPLOYEE_NAMES.length],
        period: record.period || `2026 Q${(i % 4) + 1}`,
        evaluation_date: record.evaluation_date || record.date || `2026-0${(i % 6) + 1}-15`,
        performance: Number(record.performance) || note,
        competences: Number(record.competences) || note,
        comportement: Number(record.comportement) || note,
        objectifs: Number(record.objectifs) || note,
        note_globale: Number(record.note_globale ?? record.noteGlobale) || note,
        strengths: record.strengths || 'روح فريق قوية وموثوقية عالية.',
        weaknesses: record.weaknesses || 'يحتاج إلى تحسين في الالتزام بالمواعيد.',
        objectives: record.objectives || 'إنجاز المشاريع الموكلة في الوقت المحدد.',
        comments: record.comments || 'سجل تقييم تجريبي.',
      };
    }

    if (entity === 'fleet_fuel' || entity === 'fleet_fuel_') {
      const fuelTypes = ['ديزل', 'بنزين', 'غاز'];
      const paymentMethods = ['بطاقة وقود', 'نقد', 'تحويل بنكي'];
      const qty = Number(record.quantity);
      const unit = Number(record.unit_price) || 65;
      const amount = Number(record.total_amount) || (Number.isFinite(qty) ? qty * unit : (i + 1) * 3500);
      return {
        ...record,
        vehicle_code: record.vehicle_code || `VH-${String(i + 1).padStart(3, '0')}`,
        vehicle_registration: record.vehicle_registration || `123456-${90 + i}-16`,
        purchase_date: record.purchase_date || record.date || `2026-0${(i % 6) + 1}-15`,
        driver: record.driver || DEMO_EMPLOYEE_NAMES[i % DEMO_EMPLOYEE_NAMES.length],
        fuel_type: fuelTypes.includes(record.fuel_type) ? record.fuel_type : fuelTypes[i % fuelTypes.length],
        quantity: Number.isFinite(qty) ? qty : 45 + (i % 5) * 10,
        unit_price: unit,
        total_amount: amount,
        mileage: Number(record.mileage) || 12000 + i * 500,
        station: record.station || 'محطة أرامكو',
        payment_method: paymentMethods.includes(record.payment_method) ? record.payment_method : paymentMethods[i % paymentMethods.length],
      };
    }

    if (entity === 'distribution_planning' || entity === 'distribution_planning_') {
      const statuses = ['planned', 'in_progress', 'completed'];
      const rawStatus = String(record.status || '').toLowerCase();
      const statusMap = { active: 'planned', actif: 'planned', planned: 'planned', in_progress: 'in_progress', 'en cours': 'in_progress', completed: 'completed', complete: 'completed', termine: 'completed' };
      const status = statusMap[rawStatus] || statuses[i % statuses.length];
      const deliveries = Number(record.planned_deliveries ?? record.total_deliveries);
      const distance = Number(record.total_distance ?? record.total_distance_km);
      return {
        ...record,
        plan_number: record.plan_number || `PLAN-${String(i + 1).padStart(3, '0')}`,
        plan_date: record.plan_date || `2026-0${(i % 6) + 1}-${10 + (i % 18)}`,
        zone: record.zone || record.zone_id || `ZN-${String(i + 1).padStart(3, '0')}`,
        route_id: record.route_id || `RT-${String(i + 1).padStart(3, '0')}`,
        driver_id: record.driver_id || DEMO_EMPLOYEE_NAMES[i % DEMO_EMPLOYEE_NAMES.length],
        vehicle_id: record.vehicle_id || `123456-${90 + i}-16`,
        start_time: record.start_time || record.estimated_start_time || `0${8 + (i % 2)}:00`,
        end_time: record.end_time || record.estimated_end_time || `1${2 + (i % 3)}:30`,
        planned_deliveries: Number.isFinite(deliveries) ? deliveries : 8 + (i % 5) * 3,
        total_distance: Number.isFinite(distance) ? distance : 45 + i * 12,
        status,
      };
    }

    if (entity === 'distribution_zones' || entity === 'distribution_zones_') {
      const regions = ['منطقة الرياض', 'منطقة مكة المكرمة', 'المنطقة الشرقية', 'منطقة المدينة المنورة', 'منطقة القصيم'];
      const cities = ['الرياض', 'جدة', 'الدمام', 'المدينة المنورة', 'بريدة'];
      const frequencies = ['daily', 'weekly', 'biweekly'];
      const pop = Number(record.population);
      const area = Number(record.coverage_area);
      const rawStatus = String(record.status || '').toLowerCase();
      const status = rawStatus === 'inactive' ? 'inactive' : 'active';
      return {
        ...record,
        zone_code: record.zone_code || `ZN-${String(i + 1).padStart(3, '0')}`,
        zone_name: record.zone_name || record.name || `منطقة ${i + 1}`,
        region: record.region || record.wilaya || regions[i % regions.length],
        commune: record.commune || cities[i % cities.length],
        coverage_area: Number.isFinite(area) ? area : 45 + (i % 8) * 15,
        population: Number.isFinite(pop) ? pop : 25000 + i * 8000,
        delivery_frequency: frequencies.includes(record.delivery_frequency) ? record.delivery_frequency : frequencies[i % frequencies.length],
        average_delivery_time: Number(record.average_delivery_time) || 25 + (i % 5) * 5,
        status,
      };
    }

    if (entity === 'livraison_ongoing' || entity === 'livraison_ongoing_') {
      const statuses = ['planned', 'preparation', 'in_transit', 'delivered', 'delayed'];
      const rawStatus = String(record.status || '').toLowerCase();
      const statusMap = {
        active: 'planned', actif: 'planned', planned: 'planned', planifie: 'planned',
        prepare: 'preparation', preparation: 'preparation',
        en_transit: 'in_transit', in_transit: 'in_transit',
        livre: 'delivered', delivered: 'delivered',
        retard: 'delayed', delayed: 'delayed',
      };
      const status = statusMap[rawStatus] || (statuses.includes(rawStatus) ? rawStatus : statuses[i % statuses.length]);
      const poids = Number(record.poidsTotal ?? record.poids_total);
      return {
        ...record,
        numeroLivraison: record.numeroLivraison || record.numero_livraison || `LIV-2026-${String(i + 1).padStart(4, '0')}`,
        numero_livraison: record.numero_livraison || record.numeroLivraison || `LIV-2026-${String(i + 1).padStart(4, '0')}`,
        commande: record.commande || record.delivery_id || `CMD-${1000 + i}`,
        client: record.client || record.name || demoClientFallback(i + 1),
        transporteur: record.transporteur || 'قوائم للوجستيات',
        chauffeur: record.chauffeur || DEMO_EMPLOYEE_NAMES[i % DEMO_EMPLOYEE_NAMES.length],
        vehicule: record.vehicule || record.assigned_vehicle || `123456-${90 + i}-16`,
        adresseLivraison: record.adresseLivraison || record.address || `${100 + i} شارع الملك فهد، الرياض`,
        datePrevue: record.datePrevue || record.date_prevue || `2026-0${(i % 6) + 1}-${10 + (i % 18)}`,
        date_prevue: record.date_prevue || record.datePrevue || `2026-0${(i % 6) + 1}-${10 + (i % 18)}`,
        poidsTotal: Number.isFinite(poids) ? poids : 120 + i * 35,
        poids_total: Number.isFinite(poids) ? poids : 120 + i * 35,
        produits: Array.isArray(record.produits) ? record.produits : [],
        status,
      };
    }

    if (entity === 'livraison_delivery_proofs' || entity === 'livraison_delivery_proofs_') {
      const proofTypes = ['signature', 'photo', 'document', 'otp'];
      const rawType = String(record.proof_type || '').toLowerCase();
      return {
        ...record,
        proof_id: /^PROOF-/i.test(String(record.proof_id || ''))
          ? record.proof_id
          : `PROOF-2026-${String(i + 1).padStart(4, '0')}`,
        delivery_id: /^LIV-/i.test(String(record.delivery_id || ''))
          ? record.delivery_id
          : `LIV-2026-${String(i + 1).padStart(4, '0')}`,
        proof_type: proofTypes.includes(rawType) ? rawType : proofTypes[i % proofTypes.length],
        received_by: record.received_by || record.signature_name || DEMO_EMPLOYEE_NAMES[i % DEMO_EMPLOYEE_NAMES.length],
        received_date: record.received_date || `2026-0${(i % 6) + 1}-${10 + (i % 18)}`,
        validation_status: ['validated', 'pending', 'rejected'].includes(String(record.validation_status))
          ? record.validation_status
          : 'validated',
      };
    }

    if (entity === 'livraison_delivery_statuses' || entity === 'livraison_delivery_statuses_') {
      const codes = ['planned', 'in_progress', 'delivered', 'failed'];
      const labels = ['مخطط', 'في الطريق', 'تم التسليم', 'فشل'];
      const rawCode = String(record.status_code || record.status || '').toLowerCase();
      const codeMap = {
        active: 'in_progress', actif: 'in_progress', planned: 'planned', planifie: 'planned',
        in_progress: 'in_progress', en_transit: 'in_progress', delivered: 'delivered', livre: 'delivered',
        failed: 'failed', refuse: 'failed', retard: 'failed',
      };
      const status_code = codeMap[rawCode] || codes[i % codes.length];
      const labelIdx = codes.indexOf(status_code);
      return {
        ...record,
        status_id: /^STATUS-/i.test(String(record.status_id || ''))
          ? record.status_id
          : `STATUS-2026-${String(i + 1).padStart(4, '0')}`,
        delivery_id: /^LIV-/i.test(String(record.delivery_id || ''))
          ? record.delivery_id
          : `LIV-2026-${String(i + 1).padStart(4, '0')}`,
        status_code,
        status_label: labels[labelIdx >= 0 ? labelIdx : i % labels.length],
        status_timestamp: record.status_timestamp && !/^active|actif$/i.test(String(record.status_timestamp))
          ? record.status_timestamp
          : new Date(Date.now() - i * 86400000).toISOString(),
        location: record.location || 'الرياض',
        updated_by: record.updated_by && !/^\d{4}-/.test(String(record.updated_by))
          ? record.updated_by
          : DEMO_EMPLOYEE_NAMES[i % DEMO_EMPLOYEE_NAMES.length],
      };
    }

    if (entity === 'livraison_real_time_tracking' || entity === 'livraison_real_time_tracking_') {
      const statuses = ['in_route', 'delivering', 'delayed', 'completed'];
      const priorities = ['normal', 'high', 'critical'];
      const rawStatus = String(record.status || '').toLowerCase();
      const statusMap = {
        active: 'in_route', actif: 'in_route', en_route: 'in_route', in_route: 'in_route',
        delivering: 'delivering', delayed: 'delayed', retard: 'delayed',
        completed: 'completed', livre: 'completed', delivered: 'completed',
      };
      const lat = Number(record.current_location_lat ?? record.latitude);
      const lng = Number(record.current_location_lng ?? record.longitude);
      const speed = Number(record.actual_speed_kmh);
      const dist = Number(record.distance_remaining_km);
      return {
        ...record,
        delivery_id: /^LIV-/i.test(String(record.delivery_id || ''))
          ? record.delivery_id
          : `LIV-2026-${String(i + 1).padStart(4, '0')}`,
        driver_name: record.driver_name || DEMO_EMPLOYEE_NAMES[i % DEMO_EMPLOYEE_NAMES.length],
        vehicle_registration: /^123456-/.test(String(record.vehicle_registration || ''))
          ? record.vehicle_registration
          : `123456-${90 + i}-16`,
        current_location_lat: Number.isFinite(lat) ? lat : 24.7136 + i * 0.01,
        current_location_lng: Number.isFinite(lng) ? lng : 46.6753 + i * 0.008,
        latitude: Number.isFinite(lat) ? lat : 24.7136 + i * 0.01,
        longitude: Number.isFinite(lng) ? lng : 46.6753 + i * 0.008,
        actual_speed_kmh: Number.isFinite(speed) ? speed : [0, 45, 62, 28][i % 4],
        distance_remaining_km: Number.isFinite(dist) ? dist : 12 + i * 3,
        destination_address: record.destination_address || `${100 + i} شارع الملك فهد، الرياض`,
        status: statusMap[rawStatus] || statuses[i % statuses.length],
        priority: priorities.includes(String(record.priority)) ? record.priority : priorities[i % priorities.length],
        last_update_time: record.last_update_time || record.last_update || new Date().toISOString(),
        gps_signal_strength: record.gps_signal_strength || ['strong', 'medium', 'weak'][i % 3],
      };
    }

    if (entity === 'livraison_refused_deliveries' || entity === 'livraison_refused_deliveries_') {
      const reasons = ['refused', 'absent', 'damaged', 'wrong_address'];
      const returnStatuses = ['pending_return', 'returned', 'redelivering'];
      const rawReason = String(record.refusal_reason || '').toLowerCase();
      const reason = reasons.includes(rawReason) ? rawReason : reasons[i % reasons.length];
      const rawReturn = String(record.return_status || '').toLowerCase();
      const returnMap = { active: 'pending_return', actif: 'pending_return', pending_return: 'pending_return', returned: 'returned', redelivering: 'redelivering' };
      return {
        ...record,
        refusal_id: /^REF-/i.test(String(record.refusal_id || ''))
          ? record.refusal_id
          : `REF-2026-${String(i + 1).padStart(4, '0')}`,
        delivery_id: /^LIV-/i.test(String(record.delivery_id || ''))
          ? record.delivery_id
          : `LIV-2026-${String(i + 1).padStart(4, '0')}`,
        refused_by: record.refused_by || record.name || demoClientFallback(i + 1),
        refusal_reason: reason,
        refusal_date: record.refusal_date || `2026-0${(i % 6) + 1}-${10 + (i % 18)}`,
        return_status: returnMap[rawReturn] || returnStatuses[i % returnStatuses.length],
        redelivery_scheduled: record.redelivery_scheduled === true || record.redelivery_scheduled === 1 || i % 3 === 0,
        resolution_status: ['open', 'resolved'].includes(String(record.resolution_status))
          ? record.resolution_status
          : (i % 2 === 0 ? 'open' : 'resolved'),
      };
    }

    if (entity === 'livraison_route_sheets' || entity === 'livraison_route_sheets_') {
      const statuses = ['pending', 'in_progress', 'completed', 'modified'];
      const rawStatus = String(record.status || '').toLowerCase();
      const statusMap = { active: 'in_progress', actif: 'in_progress', pending: 'pending', in_progress: 'in_progress', completed: 'completed', modified: 'modified' };
      const stops = Number(record.total_stops);
      const distance = Number(record.total_distance_km);
      return {
        ...record,
        sheet_id: /^RS-/i.test(String(record.sheet_id || record.route_sheet_id || ''))
          ? (record.sheet_id || record.route_sheet_id)
          : `RS-2026-${String(i + 1).padStart(4, '0')}`,
        driver_name: record.driver_name || DEMO_EMPLOYEE_NAMES[i % DEMO_EMPLOYEE_NAMES.length],
        vehicle_registration: /^123456-/.test(String(record.vehicle_registration || ''))
          ? record.vehicle_registration
          : `123456-${90 + i}-16`,
        route_date: record.route_date || `2026-0${(i % 6) + 1}-${10 + (i % 18)}`,
        total_stops: Number.isFinite(stops) ? stops : 5 + (i % 4),
        completed_stops: Number(record.completed_stops) || Math.max(0, (Number.isFinite(stops) ? stops : 5) - 1),
        total_distance_km: Number.isFinite(distance) ? distance : 45 + i * 12,
        status: statusMap[rawStatus] || statuses[i % statuses.length],
        departure_depot: record.departure_depot || 'مستودع قوائم - الرياض',
        return_depot: record.return_depot || 'مستودع قوائم - الرياض',
      };
    }

    if (entity === 'livraison_vehicle_loading' || entity === 'livraison_vehicle_loading_') {
      const statuses = ['preparation', 'in_progress', 'completed'];
      const rawStatus = String(record.status || '').toLowerCase();
      const statusMap = { active: 'in_progress', actif: 'in_progress', preparation: 'preparation', in_progress: 'in_progress', completed: 'completed', loaded: 'completed' };
      const weight = Number(record.total_weight_kg);
      const util = Number(record.utilization_percentage);
      const packages = Number(record.total_packages);
      return {
        ...record,
        loading_id: /^LOAD-/i.test(String(record.loading_id || ''))
          ? record.loading_id
          : `LOAD-2026-${String(i + 1).padStart(4, '0')}`,
        vehicle_registration: /^123456-/.test(String(record.vehicle_registration || ''))
          ? record.vehicle_registration
          : `123456-${90 + i}-16`,
        loading_zone: record.loading_zone && !/^(Sample |منطقة تجريبية)/i.test(String(record.loading_zone))
          ? record.loading_zone
          : `منطقة ${['أ', 'ب', 'ج', 'د'][i % 4]}`,
        loading_date: record.loading_date || `2026-0${(i % 6) + 1}-${10 + (i % 18)}`,
        total_packages: Number.isFinite(packages) ? packages : 10 + i * 3,
        total_weight_kg: Number.isFinite(weight) ? weight : 450 + i * 80,
        utilization_percentage: Number.isFinite(util) ? util : 65 + (i % 4) * 8,
        optimization_score: Number(record.optimization_score) || 70 + (i % 5) * 5,
        status: statusMap[rawStatus] || statuses[i % statuses.length],
        special_handling: record.special_handling || 'قياسي',
      };
    }

    if (entity === 'distribution_shipments' || entity === 'distribution_shipments_') {
      const statuses = ['pending', 'in_transit', 'delivered', 'cancelled'];
      const carriers = ['SMSA', 'أرامكس السعودية', 'قوائم للوجستيات', 'DHL'];
      const rawStatus = String(record.status || '').toLowerCase();
      const statusMap = { active: 'in_transit', actif: 'in_transit', pending: 'pending', in_transit: 'in_transit', delivered: 'delivered', cancelled: 'cancelled' };
      const shipNum = String(record.shipment_number || '');
      return {
        ...record,
        shipment_number: /^(SHIP|EXP)-/i.test(shipNum)
          ? shipNum
          : `EXP-2026-${String(i + 1).padStart(3, '0')}`,
        tracking_number: record.tracking_number || `TRK-${100000 + i}`,
        carrier: carriers.includes(record.carrier) ? record.carrier : carriers[i % carriers.length],
        customer_name: record.customer_name || record.contact_name || record.name || demoClientFallback(i + 1),
        customer_address: record.customer_address || record.destination || `${100 + i} شارع الملك فهد، الرياض`,
        shipping_date: record.shipping_date || record.shipment_date || `2026-0${(i % 6) + 1}-${10 + (i % 18)}`,
        status: statusMap[rawStatus] || statuses[i % statuses.length],
      };
    }

    if (entity === 'distribution_complaints' || entity === 'distribution_complaints_') {
      const statuses = ['open', 'in_progress', 'resolved', 'closed'];
      const types = ['delivery_delay', 'damaged_package', 'incomplete_delivery', 'poor_service'];
      const rawStatus = String(record.status || '').toLowerCase();
      const statusMap = { active: 'open', actif: 'open', open: 'open', in_progress: 'in_progress', resolved: 'resolved', closed: 'closed' };
      const num = String(record.complaint_number || '');
      return {
        ...record,
        complaint_number: /^(REC|COMP)-/i.test(num)
          ? num
          : `REC-2026-${String(i + 1).padStart(3, '0')}`,
        customer_name: record.customer_name || record.name || demoClientFallback(i + 1),
        complaint_type: types.includes(record.complaint_type) ? record.complaint_type : types[i % types.length],
        status: statusMap[rawStatus] || statuses[i % statuses.length],
        complaint_date: record.complaint_date || `2026-0${(i % 6) + 1}-${10 + (i % 18)}`,
        priority: ['low', 'medium', 'high'].includes(String(record.priority)) ? record.priority : ['medium', 'high', 'low'][i % 3],
      };
    }

    if (entity === 'fleet_drivers' || entity === 'fleet_drivers_') {
      const name = record.name || record.task_name || record.driver_name || record.assigned_driver || DEMO_EMPLOYEE_NAMES[i % DEMO_EMPLOYEE_NAMES.length];
      return {
        ...record,
        name,
        license_number: record.license_number || `PERM-${10000 + i}`,
        phone: record.phone || `+966 55${String(1000000 + i).slice(-7)}`,
        status: record.status || 'active',
      };
    }

    if (entity === 'fleet_vehicles' || entity === 'fleet_vehicles_') {
      const registration = record.registration || record.immatriculation || record.vehicule_registration || record.assigned_vehicle || `123456-${90 + i}-16`;
      return {
        ...record,
        type: record.type || 'van',
        registration,
        immatriculation: record.immatriculation || registration,
        vehicule_registration: record.vehicule_registration || registration,
        status: record.status || 'active',
      };
    }

    if (entity === 'fleet_gps_tracking' || entity === 'fleet_gps_tracking_') {
      const statuses = ['moving', 'stopped', 'idle', 'parking'];
      const rawStatus = String(record.status || '').toLowerCase();
      const status = statuses.includes(rawStatus) ? rawStatus : statuses[i % statuses.length];
      const lat = Number(record.latitude);
      const lng = Number(record.longitude);
      const speed = Number(record.speed);
      return {
        ...record,
        vehicle_registration: record.vehicle_registration || `123456-${90 + i}-16`,
        tracking_date: record.tracking_date || `2026-0${(i % 6) + 1}-15T0${(i % 9) + 8}:30:00`,
        latitude: Number.isFinite(lat) ? lat : 24.7136 + i * 0.012,
        longitude: Number.isFinite(lng) ? lng : 46.6753 + i * 0.008,
        speed: Number.isFinite(speed) ? speed : [0, 45, 72, 28][i % 4],
        heading: Number(record.heading) || (i * 45) % 360,
        altitude: Number(record.altitude) || 12 + i,
        address: record.address || `${100 + i} شارع التحلية، جدة`,
        status,
        engine_status: ['on', 'off', 'idle'][i % 3],
        fuel_level: Number(record.fuel_level) || 40 + (i % 6) * 8,
        odometer: Number(record.odometer) || 12000 + i * 320,
      };
    }

    return record;
  }

  _applyEntityBucketAliases() {
    for (const [canonical, alias] of Object.entries(ENTITY_BUCKET_ALIASES)) {
      const hasCanonical = Array.isArray(this.data[canonical]) && this.data[canonical].length > 0;
      const aliasRows = this.data[alias];
      if (hasCanonical || !Array.isArray(aliasRows)) continue;
      this.data[canonical] = aliasRows.map((row, index) =>
        this._normalizeHrDemoRecord(canonical, row, index)
      );
    }
  }

  _resolveEntityBucket(entity) {
    const rows = this.data[entity];
    if (Array.isArray(rows) && rows.length > 0) return entity;
    const alias = ENTITY_BUCKET_ALIASES[entity];
    if (alias && Array.isArray(this.data[alias]) && this.data[alias].length > 0) {
      if (!Array.isArray(this.data[entity]) || this.data[entity].length === 0) {
        this.data[entity] = this.data[alias].map((row, index) =>
          this._normalizeHrDemoRecord(entity, row, index)
        );
      }
      return entity;
    }
    return entity;
  }

  _normalizeLoadedRecord(record, entity, index = 0) {
    if (!record || typeof record !== 'object') return record;
    let out = { ...record };
    const bucket = String(entity || '');
    if (out.status != null && !bucket.startsWith('livraison_')) {
      out.status = normalizeRecordStatus(out.status);
    }
    out = this._normalizeHrDemoRecord(entity, out, index);
    return out;
  }

  _normalizeAllLoadedData() {
    for (const entity of Object.keys(this.data)) {
      const val = this.data[entity];
      if (Array.isArray(val)) {
        this.data[entity] = val.map((row, index) => this._normalizeLoadedRecord(row, entity, index));
      }
    }
  }

  async _loadInitialData() {
    try {
      let entities = getAllSeedEntities();
      try {
        const manifestResp = await fetch('/data/demo-manifest.json');
        if (manifestResp.ok) {
          const manifest = await manifestResp.json();
          if (Array.isArray(manifest.entities)) {
            entities = [...new Set([...entities, ...manifest.entities])];
          }
        }
      } catch (_) {
        /* manifest optional in dev */
      }

      const promises = entities.map(async (entity) => {
        try {
          const response = await fetch(`/data/${entity}.json`);
          if (!response.ok) {
            return { entity, data: entity === 'settings' ? {} : [] };
          }
          const contentType = response.headers.get('content-type') || '';
          const text = await response.text();
          if (!contentType.includes('json') && text.trimStart().startsWith('<')) {
            return { entity, data: entity === 'settings' ? {} : [] };
          }
          const data = JSON.parse(text);
          return { entity, data };
        } catch (error) {
          return { entity, data: entity === 'settings' ? {} : [] };
        }
      });

      const results = await Promise.all(promises);

      results.forEach(({ entity, data }) => {
        this.data[entity] = data;
      });

      this._hydrateFromLocalStorage();
      this._normalizeAllLoadedData();
      this._applyEntityBucketAliases();
      this.data = synthesizeAllMockData(this.data, 20);

      console.log('📦 Demo data loaded:', Object.keys(this.data).length, 'entities');
    } catch (error) {
      console.error('Error loading initial data:', error);
      throw error;
    }
  }

  async getAll(entity) {
    await this.initialize();
    const bucket = this._resolveEntityBucket(entity);
    const val = this.data[bucket];
    if (Array.isArray(val)) return [...val].sort(compareByNewestFirst);
    return [];
  }

  async getById(entity, id) {
    await this.initialize();
    const bucket = this._resolveEntityBucket(entity);
    const items = this.data[bucket] || [];
    if (!Array.isArray(items)) return null;
    return items.find((item) => item.id === id || item.id === String(id)) || null;
  }

  async create(entity, item) {
    await this.initialize();
    const bucket = this._resolveEntityBucket(entity);

    if (!this.data[bucket]) {
      this.data[bucket] = [];
    }

    const normalized = normalizeRecord(bucket, item || {});
    const items = Array.isArray(this.data[bucket]) ? this.data[bucket] : [];

    const maxId = items.reduce((max, row) => {
      const numId = parseInt(row.id, 10) || 0;
      return numId > max ? numId : max;
    }, 0);

    const newItem = this._stampTimestamps({
      ...normalized,
      id: normalized.id != null ? String(normalized.id) : String(maxId + 1),
    });
    const finalItem = this._normalizeLoadedRecord(newItem, bucket, items.length);

    if (!Array.isArray(this.data[bucket])) {
      this.data[bucket] = [];
    }
    this.data[bucket].unshift(finalItem);
    this._markDirty(bucket);
    this._persist();

    console.log(`✅ Created ${bucket}:`, finalItem.id);
    return finalItem;
  }

  async update(entity, id, updates) {
    await this.initialize();
    const bucket = this._resolveEntityBucket(entity);

    const items = this.data[bucket] || [];
    if (!Array.isArray(items)) {
      throw new Error(ds('not_array_entity', { entity }));
    }
    const index = items.findIndex((item) => item.id === id || item.id === String(id));

    if (index === -1) {
      throw new Error(ds('entity_not_found', { entity, id }));
    }

    const merged = normalizeRecord(entity, {
      ...items[index],
      ...updates,
      id: items[index].id,
    });

    this.data[bucket][index] = this._stampTimestamps(
      this._normalizeLoadedRecord(merged, bucket, index),
      true,
    );

    console.log(`✅ Updated ${bucket}:`, id);
    this._markDirty(bucket);
    this._persist();
    return this.data[bucket][index];
  }

  async delete(entity, id) {
    await this.initialize();
    const bucket = this._resolveEntityBucket(entity);

    const items = this.data[bucket] || [];
    if (!Array.isArray(items)) {
      throw new Error(ds('not_array_entity', { entity }));
    }
    const index = items.findIndex((item) => item.id === id || item.id === String(id));

    if (index === -1) {
      throw new Error(ds('entity_not_found', { entity, id }));
    }

    const deleted = items.splice(index, 1)[0];
    this._markDirty(bucket);
    this._persist();

    console.log(`✅ Deleted ${bucket}:`, id);
    return deleted;
  }

  async bulkDelete(entity, ids) {
    await this.initialize();
    const bucket = this._resolveEntityBucket(entity);
    const idSet = new Set((ids || []).map(String));
    const items = this.data[bucket] || [];
    if (!Array.isArray(items)) return { success: true, deleted: 0 };
    const before = items.length;
    this.data[bucket] = items.filter((item) => !idSet.has(String(item.id)));
    const deleted = before - this.data[bucket].length;
    this._markDirty(bucket);
    this._persist();
    return { success: true, deleted };
  }

  async search(entity, query, field = 'name') {
    await this.initialize();

    const items = this.data[entity] || [];
    const lowerQuery = query.toLowerCase();

    return items.filter((item) => {
      const value = item[field];
      if (!value) return false;
      return String(value).toLowerCase().includes(lowerQuery);
    });
  }

  async filter(entity, filterFn) {
    await this.initialize();

    const items = this.data[entity] || [];
    return items.filter(filterFn);
  }

  async count(entity) {
    await this.initialize();
    const bucket = this._resolveEntityBucket(entity);
    const val = this.data[bucket];
    if (Array.isArray(val)) return val.length;
    return 0;
  }

  async reset() {
    try {
      localStorage.removeItem(DEMO_ENTITY_PERSIST_KEY);
    } catch (_) {
      /* ignore */
    }
    this.data = {};
    this.isInitialized = false;
    this.initPromise = null;
    this.dirtyKeys = new Set();
    await this.initialize();
    console.log('🔄 Demo data reset');
  }

  resetDemoData() {
    return this.reset();
  }

  exportAll() {
    return JSON.parse(JSON.stringify(this.data));
  }

  async getSettings() {
    await this.initialize();
    const s = this.data.settings;
    if (!s || typeof s !== 'object' || Array.isArray(s)) return {};
    return s;
  }

  async updateSettings(updates) {
    await this.initialize();
    this.data.settings = {
      ...(typeof this.data.settings === 'object' && !Array.isArray(this.data.settings) ? this.data.settings : {}),
      ...updates,
    };
    this._markDirty('settings');
    this._persist();
    return this.data.settings;
  }

  async getConfig(entity) {
    await this.initialize();
    const val = this.data[entity];
    if (val && typeof val === 'object' && !Array.isArray(val)) return val;
    return {};
  }

  async updateConfig(entity, updates) {
    await this.initialize();
    const existing = await this.getConfig(entity);
    this.data[entity] = { ...existing, ...updates };
    this._markDirty(entity);
    this._persist();
    return this.data[entity];
  }

  async save(entity, data) {
    return await this.create(entity, data);
  }

  async saveAll(entity, items) {
    await this.initialize();
    this.data[entity] = items;
    this._markDirty(entity);
    this._persist();
    return items;
  }

  async get(entity) {
    return await this.getAll(entity);
  }

  async set(entity, data) {
    await this.initialize();
    this.data[entity] = data;
    this._persist();
    return data;
  }
}

const demoStorageService = new DemoStorageService();

export default demoStorageService;
